import React, { useMemo } from 'react';
import { useParams, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Award, Brain, Users as UsersIcon, Zap, Target, BookOpen, Activity, Heart, Music, Dumbbell, Sparkles, ChevronDown, BarChart2 } from 'lucide-react';
import { skipToken } from '@reduxjs/toolkit/query';
import { getAIInsights } from '../../../data/mockData';
import KVSLoader from '../../../../../components/loaders/KVSLoader';
import {
  mapAvgStudentAnalyticsToHolisticStudent,
  useGetAvgAnalyticsStudentQuery,
  parseTermFilter,
  mergeAvgStudentAnalyticsResponses,
  termFilterToQueryValue,
} from '../../../services/holisticApi';

const tabs = [
  { label: 'Overview', tab: 'overview', icon: Activity },
  { label: 'Competency', tab: 'competency', icon: BarChart2 },
  { label: 'Behavior', tab: 'behavior', icon: Brain },
  { label: 'Peers', tab: 'peers', icon: UsersIcon },
  { label: 'Parents', tab: 'parents', icon: Heart },
  { label: 'Teacher Obs.', tab: 'teacher', icon: BookOpen },
  { label: 'Interests', tab: 'interests', icon: Music },
  { label: 'Physical Dev.', tab: 'physical', icon: Dumbbell },
];

const LEVEL_CONFIG: Record<string, {
  heroBg: string; heroText: string; heroBorder: string;
  avatarBg: string; badge: string; accentBar: string;
}> = {
  Excellent: {
    heroBg: 'from-amber-50 via-yellow-50/70 to-indigo-50/30',
    heroText: 'text-amber-900',
    heroBorder: 'border-amber-200/80',
    avatarBg: 'from-indigo-500 to-violet-600',
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200/60',
    accentBar: 'from-amber-400 via-yellow-400 to-indigo-400',
  },
  Good: {
    heroBg: 'from-amber-50 via-yellow-50/70 to-violet-50/30',
    heroText: 'text-amber-900',
    heroBorder: 'border-amber-200/80',
    avatarBg: 'from-violet-500 to-purple-600',
    badge: 'bg-violet-50 text-violet-700 border-violet-200/60',
    accentBar: 'from-amber-400 via-yellow-400 to-violet-400',
  },
  Average: {
    heroBg: 'from-amber-50 via-yellow-50/70 to-orange-50/30',
    heroText: 'text-amber-900',
    heroBorder: 'border-amber-200/80',
    avatarBg: 'from-amber-400 to-orange-500',
    badge: 'bg-amber-50 text-amber-700 border-amber-200/60',
    accentBar: 'from-amber-400 via-yellow-400 to-orange-400',
  },
  'Below Average': {
    heroBg: 'from-amber-50 via-yellow-50/70 to-rose-50/30',
    heroText: 'text-amber-900',
    heroBorder: 'border-amber-200/80',
    avatarBg: 'from-rose-400 to-pink-500',
    badge: 'bg-rose-50 text-rose-700 border-rose-200/60',
    accentBar: 'from-amber-400 via-yellow-400 to-rose-400',
  },
};

const StatChip: React.FC<{ icon: React.ReactNode; label: string; value: string | number; chipBg: string; iconColor: string }> = ({ icon, label, value, chipBg, iconColor }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${chipBg}`}>
    <span className={iconColor}>{icon}</span>
    <div>
      <p className="text-xs md:text-sm text-gray-400 leading-none mb-0.5">{label}</p>
      <p className="text-base md:text-lg font-semibold text-gray-800 leading-none">{value}</p>
    </div>
  </div>
);

const ScoreBar: React.FC<{ label: string; value: number; gradient: string }> = ({ label, value, gradient }) => (
  <div className="space-y-1.5">
    <div className="flex justify-between text-xs">
      <span className="text-gray-500 font-medium">{label}</span>
      <span className="font-bold text-gray-700">{value}</span>
    </div>
    <div className="h-2 bg-white/60 rounded-full overflow-hidden border border-gray-100">
      <div
        className={`h-full rounded-full bg-gradient-to-r ${gradient}`}
        style={{ width: `${Math.min(100, value)}%`, transition: 'width 1s ease' }}
      />
    </div>
  </div>
);

const INSIGHT_THEMES = [
  { cardBg: 'from-indigo-50 to-violet-50', border: 'border-indigo-100', bar: 'from-indigo-400 to-violet-500', tagBg: 'bg-indigo-100', tagText: 'text-indigo-700', tag: 'Strength', icon: '💡' },
  { cardBg: 'from-violet-50 to-purple-50', border: 'border-violet-100', bar: 'from-violet-400 to-purple-500', tagBg: 'bg-violet-100', tagText: 'text-violet-700', tag: 'Insight', icon: '📊' },
  { cardBg: 'from-amber-50 to-orange-50', border: 'border-amber-100', bar: 'from-amber-400 to-orange-400', tagBg: 'bg-amber-100', tagText: 'text-amber-700', tag: 'Focus Area', icon: '🎯' },
  { cardBg: 'from-rose-50 to-pink-50', border: 'border-rose-100', bar: 'from-rose-400 to-pink-400', tagBg: 'bg-rose-100', tagText: 'text-rose-700', tag: 'Action', icon: '⚡' },
];

const InsightCard: React.FC<{ text: string; index: number }> = ({ text, index }) => {
  const t = INSIGHT_THEMES[index % INSIGHT_THEMES.length];
  return (
    <div className={`relative overflow-hidden rounded-xl border ${t.border} bg-gradient-to-br ${t.cardBg} shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 h-full flex flex-col justify-between`}>
      {/* Top gradient line */}
      <div className={`h-1 w-full bg-gradient-to-r ${t.bar}`} />
      <div className="p-4 flex-1 flex flex-col justify-between">
        <div>
          <span className={`inline-block px-2 py-0.5 text-[10px] font-extrabold rounded-md mb-2.5 ${t.tagBg} ${t.tagText} uppercase tracking-wider`}>
            {t.icon} {t.tag}
          </span>
          <p className="text-sm font-semibold text-gray-700 leading-relaxed">{text}</p>
        </div>
      </div>
    </div>
  );
};

const StudentProfile: React.FC = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const academicYear = Number(query.get('year') || '2025');
  const term = parseTermFilter(query.get('term'));
  const isOverall = term === 'overall';
  const termValue = termFilterToQueryValue(term);

  const singleTermQuery = useGetAvgAnalyticsStudentQuery(
    !isOverall ? { studentId: String(studentId || ''), academicYear, term } : skipToken,
    { skip: !studentId },
  );
  const term1Query = useGetAvgAnalyticsStudentQuery(
    isOverall ? { studentId: String(studentId || ''), academicYear, term: 1 } : skipToken,
    { skip: !studentId },
  );
  const term2Query = useGetAvgAnalyticsStudentQuery(
    isOverall ? { studentId: String(studentId || ''), academicYear, term: 2 } : skipToken,
    { skip: !studentId },
  );

  const effectiveData = useMemo(() => {
    if (!isOverall) return singleTermQuery.data;
    return mergeAvgStudentAnalyticsResponses(term1Query.data, term2Query.data);
  }, [isOverall, singleTermQuery.data, term1Query.data, term2Query.data]);

  const isLoading = isOverall ? (term1Query.isLoading || term2Query.isLoading) : singleTermQuery.isLoading;
  const student = mapAvgStudentAnalyticsToHolisticStudent(effectiveData);

  if (isLoading) {
    return <div className="flex min-h-[240px] items-center justify-center p-6"><KVSLoader loading size={72} /></div>;
  }

  if (!student) {
    return (
      <div className="p-12 text-center">
        <p className="text-gray-500">Student not found</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-indigo-600 underline text-sm">Go Back</button>
      </div>
    );
  }

  const insights = getAIInsights(student);
  const lvl = LEVEL_CONFIG[student.competencyLevel] || LEVEL_CONFIG['Average'];

  const pathParts = location.pathname.split('/');
  const lastPart = pathParts[pathParts.length - 1];
  const tabNames = tabs.map(t => t.tab);
  const basePath = tabNames.includes(lastPart) ? pathParts.slice(0, -1).join('/') : location.pathname;
  const initials = student.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-5">
      {/* Back */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-indigo-600 transition">
        <ArrowLeft size={14} /> Back
      </button>

      {/* ── Profile Hero ─────────────────────────────── */}
      <div className={`rounded-2xl border ${lvl.heroBorder} bg-gradient-to-br ${lvl.heroBg} overflow-hidden shadow-sm`}>
        {/* Accent gradient bar */}
        <div className={`h-1 bg-gradient-to-r ${lvl.accentBar}`} />

        <div className="p-6 flex flex-wrap items-start gap-6">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${lvl.avatarBg} flex items-center justify-center text-xl font-black text-white shadow-md`}>
              {initials}
            </div>
            {student.needsSupport && (
              <span className="absolute -bottom-1.5 -right-1.5 w-5 h-5 bg-rose-500 border-2 border-white rounded-full flex items-center justify-center">
                <Zap size={9} className="text-white" />
              </span>
            )}
          </div>

          {/* Name & meta */}
          <div className="flex-1 min-w-[180px]">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className={`text-5xl font-bold ${lvl.heroText}`}>{student.name}</h1>
              <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${lvl.badge}`}>
                {student.competencyLevel}
              </span>
              {student.needsSupport && (
                <span className="text-2xl font-semibold px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200">
                  Needs Support
                </span>
              )}
            </div>
            <p className="text-2xl text-gray-400 mb-4">
              {student.id} &middot; {student.className} {student.section} &middot; Roll #{student.rollNo}
            </p>

            {/* Stat chips */}
            <div className="flex flex-wrap gap-3">
              <StatChip icon={<Award size={30} />} label="Competency" value={student.competencyLevel} chipBg="bg-yellow-50/90 border-yellow-100/80" iconColor="text-indigo-500" />
              <StatChip icon={<Brain size={30} />} label="Behavior" value={`${student.behaviorScore}/100`} chipBg="bg-yellow-50/90 border-yellow-100/80" iconColor="text-violet-500" />
              <StatChip icon={<UsersIcon size={30} />} label="Peer" value={`${student.peerScore}/100`} chipBg="bg-yellow-50/90 border-yellow-100/80" iconColor="text-purple-500" />
              <StatChip icon={<Target size={30} />} label="Attendance" value={`${student.attendancePercentage}%`} chipBg="bg-yellow-50/90 border-yellow-100/80" iconColor="text-amber-600" />
            </div>
          </div>

          {/* Right: score bars + term select */}
          <div className="flex flex-col gap-3 min-w-[210px]">
            {/* Term selector */}
            <div className="flex items-center gap-2 bg-yellow-50/50 backdrop-blur-sm border border-yellow-100/50 rounded-lg px-3 py-1.5 shadow-sm w-fit">
              <label htmlFor="term-sel" className="text-xs font-semibold text-amber-800/80">Term</label>
              <select
                id="term-sel"
                value={termValue}
                onChange={(e) => navigate(`${location.pathname}?year=${academicYear}&term=${e.target.value}`)}
                className="text-sm font-semibold text-amber-900 bg-transparent focus:outline-none cursor-pointer"
              >
                <option value="overall">Overall</option>
                <option value="1">Term 1</option>
                <option value="2">Term 2</option>
              </select>
              <ChevronDown size={13} className="text-amber-600/70" />
            </div>

            {/* Score bars */}
            <div className="bg-yellow-50/40 backdrop-blur-sm border border-yellow-100/50 rounded-xl p-4 space-y-3 shadow-sm">
              <ScoreBar label="Term 1" value={student.academic.term1_competency} gradient="from-indigo-400 to-violet-500" />
              <ScoreBar label="Term 2" value={student.academic.term2_competency} gradient="from-violet-400 to-purple-500" />
              <ScoreBar label="Behavior" value={student.behaviorScore} gradient="from-purple-400 to-pink-400" />
              <ScoreBar label="Peer Score" value={student.peerScore} gradient="from-amber-400 to-orange-400" />
            </div>
          </div>
        </div>
      </div>

      {/* ── AI Insights ───────────────────────────────── */}
      <div className="rounded-2xl border border-indigo-100 bg-white shadow-sm overflow-hidden grid grid-cols-1 md:grid-cols-3">
        {/* Left Side: AI Assistant Spotlight Card */}
        <div className="p-6 bg-gradient-to-br from-indigo-50/80 via-violet-50/50 to-white flex flex-col justify-between relative overflow-hidden border-r border-indigo-100/50">
          {/* Subtle background glow */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-200/10 rounded-full blur-3xl -mr-16 -mt-16" />

          <div className="relative space-y-4">
            <div className="flex items-center gap-2">
              <span className="flex h-1.5 w-1.5 rounded-full bg-indigo-500" />
              <span className="text-[10px] font-bold text-indigo-500 tracking-widest uppercase">AI Academic Assistant</span>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-sm">
                  <Sparkles size={14} className="text-white" />
                </div>
                <h2 className="text-sm font-extrabold text-indigo-950 tracking-tight">Personalized Insights</h2>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed pt-1">
                Analyzing behavior metrics, peer dynamics, attendance rates, and term academic performance for <strong className="text-indigo-950 font-semibold">{student.name}</strong>.
              </p>
            </div>
          </div>

          <div className="relative pt-4 border-t border-indigo-100/50 mt-5 md:mt-0">
            <div className="flex items-center gap-2 text-[10px] text-gray-400">
              <span className="text-indigo-500">💡</span>
              <span>Review action plans with parents for focus areas.</span>
            </div>
          </div>
        </div>

        {/* Right Side: Insights List/Grid */}
        <div className="p-6 md:col-span-2 flex flex-col justify-center bg-gray-50/30">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {insights.map((ins, i) => {
              const isSoleInsight = insights.length === 1;
              return (
                <div key={i} className={isSoleInsight ? "sm:col-span-2" : ""}>
                  <InsightCard text={ins} index={i} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Tabs ──────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 px-4 bg-gradient-to-r from-gray-50/80 to-indigo-50/30">
          <nav className="flex -mb-px overflow-x-auto">
            {tabs.map(t => (
              <NavLink
                key={t.tab}
                to={`${basePath}/${t.tab}${location.search}`}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${isActive
                    ? 'border-indigo-500 text-indigo-600 bg-indigo-50/40'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200 hover:bg-gray-50/50'
                  }`
                }
              >
                <t.icon size={13} />
                {t.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="p-6">
          <Outlet context={{ student }} />
        </div>
      </div>
    </div>
  );
};

export default StudentProfile;
